//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import {
  PoolRolloverActorToString,
  PoolRolloverState,
  PoolRolloverStateToString,
  PoolStateToString,
  PoolTypeEnum,
  RolloverTypeEnum,
  RolloverTypeToString,
  yupErc20WeiRequired,
  yupEthAddrRequired,
} from './Enums'
import { LoanCTO, LoanCTOSchema } from './LoanCTO'
import { PoolBaseOverviewCTO, PoolBaseOverviewCTOSchemaValues } from './PoolBaseCTO'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export const PoolOverviewCTOSchema = yup
  .object({
    closeOfBusinessTime: yup
      .number()
      .required()
      .label('closeOfBusinessTime')
      .meta(
        toFieldMetaData({
          propName: 'closeOfBusinessTime',
          description: 'The close of business time in seconds from 0:00 UTC e.g. 64800 is 18:00 UTC',
          stringType: StringTypeEnum.ETH_TIME_OF_DAY,
        })
      ),
    earlyWithdrawFeeBps: yupErc20WeiRequired
      .label('Early Withdraw Fees BPS')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'earlyWithdrawFeeBps',
          description: 'Early Withdraw Fees in basis points',
          stringType: StringTypeEnum.BPS,
        })
      ),
    feeVaultAddr: yupEthAddrRequired
      .label('Fee Vault Address')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'feeVaultAddr',
          stringType: StringTypeEnum.ETH_ADDR,
          description: 'The smart-contract address for the Fee Vault. ',
        })
      ),
    withdrawControllerAddr: yupEthAddrRequired
      .label('Withdraw Controller Address')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'withdrawControllerAddr',
          description: 'The smart-contract address for the Withdrawal Controller',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    borrowerVaultAddr: yupEthAddrRequired
      .label('Borrower Vault Address')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'borrowerVaultAddr',
          description: 'The smart-contract address of the Borrower Vault',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    maxCapacity: yupErc20WeiRequired
      .label('Max Vault Capacity')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'maxCapacity',
          description:
            'The maximum outstanding loan principal for the loan vault. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    settledLoans: yup
      .array()
      .of(yupEthAddrRequired.required())
      .label('Loan Addresses')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'settledLoans',
          description: 'Addresses of loans that are settled',
          stringType: StringTypeEnum.ETH_ADDR,
        })
      ),

    state: yup
      .number()
      .required()
      .label('State')
      .meta(toFieldMetaData({ propName: 'state', stateToString: PoolStateToString })),

    rolloverState: yup
      .number()
      .label('rolloverState')
      .required()
      .meta(toFieldMetaData({ propName: 'rolloverState', stateToString: PoolRolloverStateToString })),
    rolloverActor: yup
      .number()
      .required()
      .label('rolloverActor')

      .meta(toFieldMetaData({ propName: 'rolloverActor', stateToString: PoolRolloverActorToString })),
    rolloverTimeToActionWindow: yup
      .number()
      .required()
      .default(0)
      .label('rolloverTimeToActionWindow')
      .meta(
        toFieldMetaData({ propName: 'rolloverTimeToActionWindow', stringType: StringTypeEnum.TIME_INTERVAL_SECONDS })
      ),
    rolloverTimeLeftInActionWindow: yup
      .number()
      .required()
      .default(0)
      .label('rolloverTimeLeftInActionWindow')
      .meta(
        toFieldMetaData({
          propName: 'rolloverTimeLeftInActionWindow',
          stringType: StringTypeEnum.TIME_INTERVAL_SECONDS,
        })
      ),
    rolloverTimePastActionWindow: yup
      .number()
      .optional()
      .default(0)
      .label('rolloverTimePastActionWindow')
      .meta(
        toFieldMetaData({ propName: 'rolloverTimePastActionWindow', stringType: StringTypeEnum.TIME_INTERVAL_SECONDS })
      ),

    totalAvailableAssets: yupErc20WeiRequired
      .label('Total USDC in Pool')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalAvailableAssets',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalAvailableSupply: yupErc20WeiRequired
      .label('Total Vault Tokens Outstanding ')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalAvailableSupply',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    currentExpectedInterest: yupErc20WeiRequired
      .label('Current Expected Interest')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'currentExpectedInterest',
          description:
            'The amount of interest in USDC-1e6 expected to accrue on the active loan cycle if held to maturity',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    liquidityPoolAssets: yupErc20WeiRequired
      .label('Collateral Value')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'liquidityPoolAssets',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalAssets: yupErc20WeiRequired
      .label('Total Balance of Liquidity Pool')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalAssets',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    totalAssetsDeposited: yupErc20WeiRequired
      .label('Total Amount Loaned To Date')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsDeposited',
          description: 'The total amount of USDC-1e6 loaned in the Loan Vault to date',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalAssetsWithdrawn: yupErc20WeiRequired
      .label('Total Amount Withdrawn')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalAssetsWithdrawn',
          description: ' The total amount of USDC-1e6 withdrawn fro the Loan Vault to date ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalRequestedShares: yupErc20WeiRequired
      .label('Total Outstanding Withdrawal Requests (Tokens)')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalRequestedShares',
          description: 'The total amount of vault tokens in outstanding withdrawal requests',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    totalRedeemableShares: yupErc20WeiRequired
      .label('Total Tokens Redeemable Immediately ')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalRedeemableShares',
          description: ' The total amount of vault tokens which can be redeemed immediately for USDC  ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    totalRequestedAssets: yupErc20WeiRequired
      .label('Total Outstanding Withdrawal Requests (Assets)')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalRequestedAssets',
          description: ' The total amount of USDC-1e6 outstanding withdrawal requests ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalWithdrawableAssets: yupErc20WeiRequired
      .label('USDC Eligible for Immediate Withdrawal')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalWithdrawableAssets',
          description: ' The total amount of USDC-1e6 which can be withdrawn immediately ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    feeVaultBalance: yupErc20WeiRequired
      .label('Fee Vault Balance')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'feeVaultBalance',
          description: ' TODO ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    borrowerVaultBalance: yupErc20WeiRequired
      .label('Borrower Vault Balance')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'borrowerVaultBalance',
          description: ' TODO ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    borrowerWalletBalance: yupErc20WeiRequired
      .label('Borrower Wallet Balance')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'borrowerWalletBalance',
          description:
            " The USDC-1e6 balance of the Borrower's wa borrowerVaultBalance The USDC-1e6 balance  of the Borrower vault ",
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    poolBalance: yupErc20WeiRequired
      .label('Vault Balance')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'poolBalance',
          description: ' TODO ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    exchangeRateAtMaturity: yupErc20WeiRequired
      .label('exchangeRateAtMaturity')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'exchangeRateAtMaturity',
          description: ' TODO ',
          stringType: StringTypeEnum.EXCHANGE_RATE,
        })
      ),

    requestedLoanState: LoanCTOSchema.required(),
    activeLoanState: LoanCTOSchema.required(),
    maturedLoanState: LoanCTOSchema.required(),
    createdLoanState: LoanCTOSchema.required(),
    rolloverClosingLoanState: LoanCTOSchema.required(),
    rolloverOpeningLoanState: LoanCTOSchema.required(),

    redemptionLenders: yup
      .array()
      .of(yupEthAddrRequired.required())
      .required()
      .default([])
      .label('Lenders with  Pending withdraws')
      .meta(
        toFieldMetaData({ propName: 'redemptionLenders', description: ' TODO ', stringType: StringTypeEnum.ETH_ADDR })
      ),
    requestedShares: yup
      .array()
      .of(yupErc20WeiRequired.required())
      .required()
      .default([])
      .label('Pending withdraw shares')
      .meta(
        toFieldMetaData({
          propName: 'requestedShares',
          description: ' List of the number of vault tokens-1e6 for a given withdrawal request ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    redeemableShares: yup
      .array()
      .of(yupErc20WeiRequired.required())
      .required()
      .label('Redeemable shares')
      .meta(
        toFieldMetaData({
          propName: 'redeemableShares',
          description:
            ' The number of vault tokens-1e6 which can be  immediately redeemed for USDC for a given  withdrawal request ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),

    totalEarlyRequestedShares: yupErc20WeiRequired
      .label('totalEarlyRequestedShares')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalEarlyRequestedShares',
          description: ' The total amount of vault tokens-1e6 for a given  Early Withdrawal Request ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    totalEarlyRequestedAssets: yupErc20WeiRequired
      .label('*** Value at Maturity to be Accepted to Terminated Early')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalEarlyRequestedAssets',
          description: ' The principal value of the vault tokens in USDC-1e6 for a given Early Withdrawal Request ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    totalEarlyAcceptedShares: yupErc20WeiRequired
      .label('totalEarlyAcceptedShares')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalEarlyAcceptedShares',
          description: ' The number of vault tokens-1e6 to be redeem corresponding to a given Early Withdrawal Amount ',
          stringType: StringTypeEnum.PPT_SHARES,
        })
      ),
    totalEarlyAcceptedAssets: yupErc20WeiRequired
      .label('Value at Maturity accepted to be Terminated Early')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalEarlyAcceptedAssets',
          description: ' The Early Withdrawal Amount in USDC-1e6 for  given Early Withdrawal Request ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    rolloverAssetsToReturnToPool: yupErc20WeiRequired
      .label('Assets to Return to Vault (Not including fees)')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'rolloverAssetsToReturnToPool',
          description:
            ' The amount of USDC to return to the pool after rollover to meet rollover termination requests. O a positive number if there is a Net Outflow at rollover time. ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    rolloverOpeningLoanPrincipal: yupErc20WeiRequired
      .label('Rollover Loan Principal')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'rolloverOpeningLoanPrincipal',
          description: ' The principal of the rollover loan ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    rolloverClosingLoanEndingValue: yupErc20WeiRequired
      .label('Principal and Interest Value at Maturity for Active Loan')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'rolloverClosingLoanEndingValue',
          description:
            ' The principal and accrued interest for the loan cycle which is ending and rolling over to a new loan cycle ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    rolloverAssetToGetFromPool: yupErc20WeiRequired
      .label('Assets to be Drawn from Vault at Rollover')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'rolloverAssetToGetFromPool',
          description:
            ' The amount of USDC to be drawn from the Loan Vault to be included in the next loan cycle. Only  positive number if there is a Net Inflow at rollover time. ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    rolloverNetRepaymentWithFees: yupErc20WeiRequired
      .label('Assets to Return to Vault (Including fees)')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'rolloverNetRepaymentWithFees',
          description: ' The amount of USDC to be repaid on the Payment Date including Fees. ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    rolloverMaturingLoanFees: yupErc20WeiRequired
      .label('Fees Due at Maturity')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'rolloverMaturingLoanFees',
          description: ' The amount of fees owed to the Platform Provider and Investment Advisor. ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),

    rolloverAssetsFromPriorLoan: yupErc20WeiRequired
      .label('Principal and Interest Rolling Over from Prior Loan')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'rolloverAssetsFromPriorLoan',
          description:
            ' The amount of USDC including principal and interest from the prior loan cycle that is rolling over into the next loan cycle. ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    rolloverType: yup
      .mixed<RolloverTypeEnum>()
      .oneOf(Object.values(RolloverTypeEnum) as number[])
      .defined()
      .label('rolloverType')
      .meta(
        toFieldMetaData({
          propName: 'rolloverType',
          description: ' rolloverType ',
          stateToString: RolloverTypeToString,
        })
      ),

    totalEarlyWithdrawPrincipal: yupErc20WeiRequired
      .label('*** Early Withdraw Principal')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'totalEarlyWithdrawPrincipal',
          description: ' totalEarlyWithdrawPrincipal ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    poolAssetsForWithdraw: yupErc20WeiRequired
      .label('Pool Assets for Withdraw')
      .required()
      .meta(
        toFieldMetaData({
          propName: 'poolAssetsForWithdraw',
          description: ' poolAssetsForWithdraw ',
          stringType: StringTypeEnum.STABLE_COINS,
        })
      ),
    ...PoolBaseOverviewCTOSchemaValues,
  })
  .label('PoolOverviewCTO')
  .meta(toFieldMetaData({ propName: 'PoolOverviewCTO', description: 'PoolOverviewCTOSchema' }))

export interface PoolOverviewCTO extends PoolBaseOverviewCTO {
  closeOfBusinessTime: number
  earlyWithdrawFeeBps: ERC20Wei
  feeVaultAddr: EthAddr
  withdrawControllerAddr: EthAddr
  borrowerVaultAddr: EthAddr
  maxCapacity: ERC20Wei
  settledLoans: EthAddr[]
  exchangeRateAtMaturity: ERC20Wei
  totalAvailableAssets: ERC20Wei
  totalAvailableSupply: ERC20Wei
  currentExpectedInterest: ERC20Wei
  liquidityPoolAssets: ERC20Wei
  totalAssets: ERC20Wei
  totalAssetsDeposited: ERC20Wei
  totalAssetsWithdrawn: ERC20Wei
  borrowerVaultBalance: ERC20Wei
  borrowerWalletBalance: ERC20Wei
  totalRequestedShares: ERC20Wei
  totalRequestedAssets: ERC20Wei
  totalRedeemableShares: ERC20Wei
  totalWithdrawableAssets: ERC20Wei
  requestedLoanState: LoanCTO
  activeLoanState: LoanCTO
  maturedLoanState: LoanCTO
  createdLoanState: LoanCTO
  rolloverClosingLoanState: LoanCTO
  rolloverOpeningLoanState: LoanCTO
  redemptionLenders: EthAddr[]
  requestedShares: ERC20Wei[]
  redeemableShares: ERC20Wei[]
  totalEarlyRequestedShares: ERC20Wei
  totalEarlyRequestedAssets: ERC20Wei
  totalEarlyAcceptedShares: ERC20Wei
  totalEarlyAcceptedAssets: ERC20Wei
  rolloverAssetsToReturnToPool: ERC20Wei
  rolloverOpeningLoanPrincipal: ERC20Wei
  rolloverClosingLoanEndingValue: ERC20Wei
  rolloverAssetToGetFromPool: ERC20Wei
  rolloverNetRepaymentWithFees: ERC20Wei
  rolloverMaturingLoanFees: ERC20Wei
  rolloverAssetsFromPriorLoan: ERC20Wei
  totalEarlyWithdrawPrincipal: ERC20Wei
  poolAssetsForWithdraw: ERC20Wei
  rolloverState: number
  rolloverActor: number
  rolloverTimeToActionWindow: number
  rolloverTimeLeftInActionWindow: number
  rolloverTimePastActionWindow: number
  rolloverType: RolloverTypeEnum
  state: number
  feeVaultBalance: ERC20Wei
  poolBalance: ERC20Wei
}

type PoolOverviewCTOInfer = InferType<typeof PoolOverviewCTOSchema>
export const _testAPoolTermOverview: PoolOverviewCTO = {} as unknown as PoolOverviewCTOInfer
export const _testBPoolTermOverview: PoolOverviewCTOInfer = {} as unknown as PoolOverviewCTO

export const PoolSummaryDTOSchema = yup.object({
  indicativeApr: yup
    .number()
    .label('indicativeApr')
    .meta(toFieldMetaData({ propName: 'indicativeApr', stringType: StringTypeEnum.APR })),
  finalizedApr: yup
    .number()
    .label('finalizedApr')
    .meta(toFieldMetaData({ propName: 'finalizedApr', stringType: StringTypeEnum.APR })),
  tenor: yup
    .number()
    .required()
    .positive()
    .label('tenor')
    .meta(toFieldMetaData({ propName: 'tenor' })),
  matureEthDate: yup
    .number()
    .required()
    .label('matureEthDate')
    .meta(toFieldMetaData({ propName: 'matureEthDate', stringType: StringTypeEnum.ETH_DATE })),

  totalRequestedAssets: yupErc20WeiRequired.label('Total Assets in Withdrawal Requests').meta(
    toFieldMetaData({
      propName: 'totalRequestedAssets',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
})
export interface PoolSummaryDTO extends InferType<typeof PoolSummaryDTOSchema> {
  indicativeApr: number
  finalizedApr: number
}

export const poolIsOpenForInvestment = (poolOverviewCto: PoolBaseOverviewCTO, chainTimestamp: number) =>
  poolOverviewCto.poolType == PoolTypeEnum.FlexibleRate ||
  poolOverviewCto.poolType == PoolTypeEnum.Dynamic ||
  ((poolOverviewCto as PoolOverviewCTO)?.rolloverState !== PoolRolloverState.CreateNextLoan &&
    (poolOverviewCto as PoolOverviewCTO)?.rolloverState !== PoolRolloverState.EarlyRolloverFinalize &&
    (poolOverviewCto as PoolOverviewCTO)?.rolloverState !== PoolRolloverState.RolloverFinalize &&
    (poolOverviewCto as PoolOverviewCTO)?.rolloverState !== PoolRolloverState.LateRolloverFinalize &&
    chainTimestamp < (poolOverviewCto as PoolOverviewCTO).requestedLoanState.depositClosingTimestamp)
